import { useEffect, useState } from 'react';
import { AlertTriangle, Info, ShieldAlert, Server } from 'lucide-react';

interface AdminConfig {
  apiBaseUrl: string;
  environment: string;
  region: string;
  serviceVersion: string;
  gitSha: string;
  buildTime: string | null;
  features: {
    dangerZoneEnabled: boolean;
    auditLoggingEnabled: boolean;
    mfaRequired: boolean;
  };
}

export function EnvironmentBanner() {
  const [config, setConfig] = useState<AdminConfig | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch('/api/v1/admin/config', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch admin config:', error);
    }
  };

  if (!config) {
    return null;
  }

  const isProd = config.environment === 'PROD';
  const isDev = config.environment === 'DEV';
  const isStaging = config.environment === 'STAGING';

  const getBannerStyles = () => {
    if (isProd) {
      // PROD is always visible with prominent red styling
      return 'bg-red-900/90 border-red-700 text-red-100';
    }
    if (isStaging) {
      return 'bg-amber-900/80 border-amber-700 text-amber-200';
    }
    if (isDev) {
      return 'bg-blue-900/80 border-blue-700 text-blue-200';
    }
    return 'bg-slate-800 border-slate-700 text-slate-300';
  };

  const getIcon = () => {
    if (isProd) {
      return <ShieldAlert className="h-4 w-4 text-red-300" />;
    }
    if (isStaging) {
      return <AlertTriangle className="h-4 w-4" />;
    }
    if (isDev) {
      return <Info className="h-4 w-4" />;
    }
    return <Server className="h-4 w-4" />;
  };

  const getEnvLabel = () => {
    if (isProd) {
      return (
        <span className="flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
          </span>
          <span className="font-bold uppercase tracking-wide">PRODUCTION</span>
        </span>
      );
    }
    return <span className="font-semibold">{config.environment}</span>;
  };

  return (
    <div className={`border-b px-4 py-1.5 text-xs ${getBannerStyles()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {getIcon()}
          {getEnvLabel()}
          <span className="opacity-75">|</span>
          <span className="opacity-75">{config.region}</span>
          {/* Always show version in compact form */}
          <span className="opacity-50">|</span>
          <span
            className="opacity-75 font-mono cursor-help"
            title={`Version: ${config.serviceVersion}\nGit SHA: ${config.gitSha || 'dev'}\nBuild: ${config.buildTime || 'N/A'}`}
          >
            v{config.serviceVersion}
            {config.gitSha && ` (${config.gitSha.substring(0, 7)})`}
          </span>
        </div>

        <button
          onClick={() => setShowDetails(!showDetails)}
          className="opacity-75 hover:opacity-100 transition-opacity"
        >
          {showDetails ? 'Hide' : 'Details'}
        </button>
      </div>

      {showDetails && (
        <div className="mt-2 pt-2 border-t border-current/20 grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <div className="opacity-50">Version</div>
            <div className="font-mono">{config.serviceVersion}</div>
          </div>
          <div>
            <div className="opacity-50">Git SHA</div>
            <div className="font-mono">{config.gitSha?.substring(0, 7) || 'dev'}</div>
          </div>
          <div>
            <div className="opacity-50">MFA Required</div>
            <div>{config.features.mfaRequired ? 'Yes' : 'No'}</div>
          </div>
          <div>
            <div className="opacity-50">Audit Logging</div>
            <div>{config.features.auditLoggingEnabled ? 'Enabled' : 'Disabled'}</div>
          </div>
        </div>
      )}
    </div>
  );
}
